/* eslint-disable jira/filename-case */

import type { DocumentNode } from 'graphql';
// eslint-disable-next-line jira/restricted/graphql-tag
import gql from 'graphql-tag';
import { expVal } from '@atlassian/jira-feature-experiments';

// gives info needed to render tabs
export const LAYOUT_FRAGMENT: DocumentNode = gql`
	fragment LayoutFragment on JiraIssueCreateLayout {
		contentSection {
			items {
				... on JiraTabLayout {
					id
					name
					items {
						... on JiraFieldLayoutItem {
							fieldId
						}
					}
					tabId
				}
			}
		}
	}
`;

export const LAYOUT_FRAGMENT_WITH_ML: DocumentNode = gql`
	fragment LayoutFragmentWithML on JiraIssueCreateLayout {
		contentSection {
			items {
				... on JiraTabLayout {
					id
					name
					items {
						... on JiraFieldLayoutItem {
							fieldId
						}
					}
					collapsedItems {
						... on JiraFieldLayoutItem {
							fieldId
						}
					}
					tabId
				}
			}
		}
	}
`;

/**
 * Layout fragment with ML (collapsedItems) AND rankedItems.
 * Used when withML=true and show-more-relevant-fields-in-quick-create-exp cohort is 'test'.
 */
export const LAYOUT_FRAGMENT_WITH_RANKED_ITEMS: DocumentNode = gql`
	fragment LayoutFragmentWithRankedItems on JiraIssueCreateLayout {
		contentSection {
			items {
				... on JiraTabLayout {
					id
					name
					items {
						... on JiraFieldLayoutItem {
							fieldId
						}
					}
					collapsedItems {
						... on JiraFieldLayoutItem {
							fieldId
						}
					}
					rankedItems {
						... on JiraFieldLayoutItem {
							fieldId
						}
					}
					tabId
				}
			}
		}
	}
`;

/**
 * Layout fragment without ML (no collapsedItems) but WITH rankedItems.
 * Used when withML=false and show-more-relevant-fields-in-quick-create-exp cohort is 'test'.
 */
export const LAYOUT_FRAGMENT_WITH_RANKED_ITEMS_NO_ML: DocumentNode = gql`
	fragment LayoutFragmentWithRankedItemsNoML on JiraIssueCreateLayout {
		contentSection {
			items {
				... on JiraTabLayout {
					id
					name
					items {
						... on JiraFieldLayoutItem {
							fieldId
						}
					}
					rankedItems {
						... on JiraFieldLayoutItem {
							fieldId
						}
					}
					tabId
				}
			}
		}
	}
`;

/**
 * Returns the correct layout fragment based on the withML flag and the
 * show-more-relevant-fields-in-quick-create-exp experiment cohort.
 *
 * | cohort  | withML | fragment                            |
 * |---------|--------|-------------------------------------|
 * | test    | true   | LAYOUT_FRAGMENT_WITH_RANKED_ITEMS   |
 * | test    | false  | LAYOUT_FRAGMENT_WITH_RANKED_ITEMS_NO_ML |
 * | control | true   | LAYOUT_FRAGMENT_WITH_ML             |
 * | control | false  | LAYOUT_FRAGMENT                     |
 */
export function getLayoutFragment(withML: boolean | undefined): DocumentNode {
	if (expVal('show-more-relevant-fields-in-quick-create-exp', 'isEnabled', false)) {
		if (withML) {
			return LAYOUT_FRAGMENT_WITH_RANKED_ITEMS;
		}
		return LAYOUT_FRAGMENT_WITH_RANKED_ITEMS_NO_ML;
	}

	if (withML) {
		return LAYOUT_FRAGMENT_WITH_ML;
	}
	return LAYOUT_FRAGMENT;
}

export function getLayoutFragmentName(fragment: DocumentNode): string {
	const definition = fragment.definitions[0];
	if (definition?.kind !== 'FragmentDefinition') {
		throw new Error('Expected a layout fragment definition');
	}
	return definition.name.value;
}

// this info is common across all fields
export const BASE_FIELD_FRAGMENT: DocumentNode = gql`
	fragment BaseFieldFragment on JiraIssueField {
		fieldId
		name
		type
		description
		... on JiraIssueFieldConfiguration {
			fieldConfig {
				isRequired
				isEditable
				nonEditableReason {
					message
				}
			}
		}
	}
`;

// Same as BASE_FIELD_FRAGMENT, but additionally requests the issue field create context option ARI.
// Gated behind `issue_create_reuse_labels_field_for_create`; swapped in at the query-builder level so
// the extra field is only fetched when the gate is on.
// Uses a distinct fragment name (BaseFieldWithAriFragment) to avoid a duplicate-definition collision
// with BaseFieldFragment when both DocumentNodes are imported in the same module.
export const BASE_FIELD_FRAGMENT_WITH_CREATE_CONTEXT_OPTION_ARI: DocumentNode = gql`
	fragment BaseFieldWithAriFragment on JiraIssueField {
		fieldId
		name
		type
		description
		issueFieldCreateContextOptionAri
		... on JiraIssueFieldConfiguration {
			fieldConfig {
				isRequired
				isEditable
				nonEditableReason {
					message
				}
			}
		}
	}
`;

export const PROJECT_WITHOUT_CLASSIFICATION: DocumentNode = gql`
	fragment Project on JiraProject {
		id: projectId
		name
		avatar {
			large
		}
		projectType
		simplified
		key
		isAIEnabled
	}
`;

export const PROJECT_FIELD_FRAGMENT_WITHOUT_CLASSIFICATION: DocumentNode = gql`
	fragment ProjectFieldFragmentWithoutClassification on JiraProjectField {
		project {
			...Project
		}
		searchUrl
	}
	${PROJECT_WITHOUT_CLASSIFICATION}
`;

/* issuetype field */
const ISSUE_TYPE = gql`
	fragment IssueType on JiraIssueType {
		id: issueTypeId
		name
		avatar {
			large
		}
	}
`;

const ISSUE_TYPE_WITH_HIERARCHY = gql`
	fragment IssueTypeWithHierarchy on JiraIssueType {
		id: issueTypeId
		name
		avatar {
			large
		}
		hierarchy {
			level
		}
	}
`;

export const ISSUE_TYPE_FIELD_FRAGMENT: DocumentNode = gql`
	fragment IssueTypeFieldFragment on JiraIssueTypeField {
		issueType {
			...IssueType
		}
		issueTypes {
			nodes {
				...IssueType
			}
		}
	}
	${ISSUE_TYPE}
`;

export const ISSUE_TYPE_FIELD_FRAGMENT_WITH_HIERARCHY: DocumentNode = gql`
	fragment IssueTypeFieldFragmentWithHierarchy on JiraIssueTypeField {
		issueType {
			...IssueTypeWithHierarchy
		}
		issueTypes {
			nodes {
				...IssueTypeWithHierarchy
			}
		}
	}
	${ISSUE_TYPE_WITH_HIERARCHY}
`;

export const ISSUE_TYPE_FIELD_SEARCH_BY_FRAGMENT: DocumentNode = gql`
	fragment IssueTypeFieldSearchFragment on JiraIssueTypeField {
		issueType {
			...IssueType
		}
		issueTypes(searchBy: $issueTypes) {
			nodes {
				...IssueType
			}
		}
	}
	${ISSUE_TYPE}
`;

export const ISSUE_TYPE_FIELD_SEARCH_BY_FRAGMENT_WITH_HIERARCHY: DocumentNode = gql`
	fragment IssueTypeFieldSearchFragmentWithHierarchy on JiraIssueTypeField {
		issueType {
			...IssueTypeWithHierarchy
		}
		issueTypes(searchBy: $issueTypes) {
			nodes {
				...IssueTypeWithHierarchy
			}
		}
	}
	${ISSUE_TYPE_WITH_HIERARCHY}
`;

/* text field */
export const TEXT_FIELD_FRAGMENT: DocumentNode = gql`
	fragment TextFieldFragment on JiraSingleLineTextField {
		text
	}
`;

export const CONNECT_TEXT_FIELD_FRAGMENT: DocumentNode = gql`
	fragment ConnectTextFieldFragment on JiraConnectTextField {
		text
	}
`;

export const FORGE_STRING_FIELD_FRAGMENT: DocumentNode = gql`
	fragment ForgeStringFieldFragment on JiraForgeStringField {
		text
	}
`;

const MEDIA_CONTEXT_FRAGMENT = gql`
	fragment JiraMediaContextFragment on JiraMediaContext {
		uploadToken {
			... on JiraMediaUploadToken {
				endpointUrl
				clientId
				targetCollection
				token
				tokenDurationInMin
			}
			... on QueryError {
				message
			}
		}
	}
`;

const ADMIN_RICH_TEXT_FIELD_CONFIG_FRAGMENT: DocumentNode = gql`
	fragment JiraAdminRichTextFieldConfigFragment on JiraRichTextField {
		adminRichTextConfig {
			aiEnabledByProject
		}
	}
`;

export const RICH_TEXT_FIELD_FRAGMENT: DocumentNode = gql`
	fragment RichTextFieldFragment on JiraRichTextField {
		richText {
			plainText
			adfValue {
				json
			}
		}
		renderer
		mediaContext {
			...JiraMediaContextFragment
		}
		...JiraAdminRichTextFieldConfigFragment
	}
	${MEDIA_CONTEXT_FRAGMENT}
	${ADMIN_RICH_TEXT_FIELD_CONFIG_FRAGMENT}
`;

export const CONNECT_RICH_TEXT_FIELD_FRAGMENT: DocumentNode = gql`
	fragment ConnectRichTextFieldFragment on JiraConnectRichTextField {
		richText {
			plainText
			adfValue {
				json
			}
		}
		renderer
		mediaContext {
			...JiraMediaContextFragment
		}
	}
	${MEDIA_CONTEXT_FRAGMENT}
`;

export const FORGE_OBJECT_FIELD_FRAGMENT: DocumentNode = gql`
	fragment ForgeObjectFieldFragment on JiraForgeObjectField {
		object
	}
`;

/* components field */
const COMPONENT = gql`
	fragment Component on JiraComponent {
		id: componentId
		name
	}
`;

export const COMPONENTS_FIELD_FRAGMENT: DocumentNode = gql`
	fragment ComponentsFieldFragment on JiraComponentsField {
		selectedComponents {
			...Component
		}
		components {
			nodes {
				...Component
			}
		}
	}
	${COMPONENT}
`;

export const COMPONENTS_FIELD_FRAGMENT_ASYNC_OPTIONS: DocumentNode = gql`
	fragment ComponentsFieldFragmentAsyncOptions on JiraComponentsField {
		selectedComponents {
			...Component
		}
		components(first: 1) {
			nodes {
				...Component
			}
		}
	}
	${COMPONENT}
`;

/* user picker */
const USER: DocumentNode = gql`
	fragment UserFragment on User {
		accountId
		name
		picture
	}
`;

export const SINGLE_USER_PICKER_FIELD_FRAGMENT: DocumentNode = gql`
	fragment SingleUserPickerFieldFragment on JiraSingleSelectUserPickerField {
		user {
			...UserFragment
		}
		searchUrl
	}
	${USER}
`;

export const FORGE_SINGLE_USER_PICKER_FIELD_FRAGMENT: DocumentNode = gql`
	fragment ForgeSingleUserPickerFieldFragment on JiraForgeUserField {
		user {
			...UserFragment
		}
		searchUrl
	}
	${USER}
`;

export const MULTI_USER_PICKER_FIELD_FRAGMENT: DocumentNode = gql`
	fragment MultiUserPickerFieldFragment on JiraMultipleSelectUserPickerField {
		selectedUsers {
			...UserFragment
		}
		searchUrl
	}
	${USER}
`;

export const JSM_MULTI_USER_PICKER_FIELD_FRAGMENT: DocumentNode = gql`
	fragment JsmMultiUserPickerFieldFragment on JsmMultipleSelectUserPickerField {
		selectedUsers {
			...UserFragment
		}
		searchUrl
	}
	${USER}
`;

export const FORGE_MULTI_USER_PICKER_FIELD_FRAGMENT: DocumentNode = gql`
	fragment ForgeMultiUserPickerFieldFragment on JiraForgeUsersField {
		selectedUsers {
			...UserFragment
		}
		searchUrl
	}
	${USER}
`;

export const PEOPLE_FIELD_FRAGMENT: DocumentNode = gql`
	fragment PeopleFieldFragment on JiraPeopleField {
		selectedUsers {
			...UserFragment
		}
		searchUrl
		isMulti
	}
	${USER}
`;

export const JSM_PEOPLE_FIELD_FRAGMENT: DocumentNode = gql`
	fragment JsmPeopleFieldFragment on JsmPeopleField {
		selectedUsers {
			...UserFragment
		}
		searchUrl
		isMulti
	}
	${USER}
`;

/* number field */
export const NUMBER_FIELD_FRAGMENT: DocumentNode = gql`
	fragment NumberFieldFragment on JiraNumberField {
		number
		formatConfig @include(if: $withNumberFormat) {
			formatStyle
			formatUnit
			formatDecimals
		}
	}
`;

export const CONNECT_NUMBER_FIELD_FRAGMENT: DocumentNode = gql`
	fragment ConnectNumberFieldFragment on JiraConnectNumberField {
		number
	}
`;

export const FORGE_NUMBER_FIELD_FRAGMENT: DocumentNode = gql`
	fragment ForgeNumberFieldFragment on JiraForgeNumberField {
		number
	}
`;

/* date-time field */
export const DATE_TIME_FIELD_FRAGMENT: DocumentNode = gql`
	fragment DateTimePickerFieldFragment on JiraDateTimePickerField {
		dateTime
	}
`;

export const ISSUE_LINKS_FIELD_FRAGMENT_WITH_COPY_LINKS_AND_ATTACHMENTS_REFINED: DocumentNode = gql`
	fragment IssueLinksFieldFragmentWithCopyLinksAndAttachmentsRefined on JiraIssueLinkField {
		name
		searchUrl
		issueLinks {
			relatedBy {
				relationName
				linkTypeId
				linkTypeName
				direction
			}
			issue {
				id
				issueId
				key
				avatar {
					small
					medium
				}
			}
		}
		issueLinkTypeRelations {
			nodes {
				relationName
				linkTypeId
				linkTypeName
				direction
			}
		}
		copyLinksAndAttachments {
			copyLinks
			copyAttachments
		}
	}
`;

export const ISSUE_LINKS_FIELD_FRAGMENT_REFINED: DocumentNode = gql`
	fragment IssueLinksFieldFragmentRefined on JiraIssueLinkField {
		name
		searchUrl
		issueLinks {
			relatedBy {
				relationName
				linkTypeId
				linkTypeName
				direction
			}
			issue {
				id
				issueId
				key
				avatar {
					small
					medium
				}
			}
		}
		issueLinkTypeRelations {
			nodes {
				relationName
				linkTypeId
				linkTypeName
				direction
			}
		}
	}
`;

export const FORGE_DATETIME_FIELD_FRAGMENT: DocumentNode = gql`
	fragment ForgeDateTimePickerFieldFragment on JiraForgeDatetimeField {
		dateTime
	}
`;

/* date field */
export const DATE_FIELD_FRAGMENT: DocumentNode = gql`
	fragment DatePickerFieldFragment on JiraDatePickerField {
		date
	}
`;

/* epic-link field */
const EPIC: DocumentNode = gql`
	fragment Epic on JiraEpic {
		name
		key
		summary
	}
`;

export const EPIC_LINK_FIELD_FRAGMENT: DocumentNode = gql`
	fragment EpicLinkFieldFragment on JiraEpicLinkField {
		epic {
			...Epic
		}
		searchUrl
	}
	${EPIC}
`;

/* labels field */
const LABEL: DocumentNode = gql`
	fragment Label on JiraLabel {
		id: labelId
		name
	}
`;

const LABEL_WITH_COLOR: DocumentNode = gql`
	fragment LabelWithColor on JiraLabel {
		id: labelId
		name
		color {
			colorKey
		}
	}
`;

export const LABEL_FIELD_FRAGMENT: DocumentNode = gql`
	fragment LabelFieldFragment on JiraLabelsField {
		selectedLabels {
			...Label
		}
		searchUrl
	}
	${LABEL}
`;

export const LABEL_WITH_COLOR_FIELD_FRAGMENT: DocumentNode = gql`
	fragment LabelWithColorFieldFragment on JiraLabelsField {
		selectedLabels {
			...LabelWithColor
		}
		searchUrl
	}
	${LABEL_WITH_COLOR}
`;

export const FORGE_STRINGS_FIELD_FRAGMENT: DocumentNode = gql`
	fragment ForgeStringsFieldFragment on JiraForgeStringsField {
		selectedLabels {
			...Label
		}
		searchUrl
	}
	${LABEL}
`;

/* versions field */
const VERSION = gql`
	fragment Version on JiraVersion {
		id: versionId
		name
		status
	}
`;

export const VERSION_FIELD_FRAGMENT: DocumentNode = gql`
	fragment VersionFieldFragment on JiraSingleVersionPickerField {
		version {
			...Version
		}
		versions {
			nodes {
				...Version
			}
		}
	}
	${VERSION}
`;

export const VERSION_FIELD_FRAGMENT_ASYNC_OPTIONS: DocumentNode = gql`
	fragment VersionFieldFragmentAsyncOptions on JiraSingleVersionPickerField {
		version {
			...Version
		}
		versions(first: 1) {
			nodes {
				...Version
			}
		}
	}
	${VERSION}
`;

export const MULTI_VERSION_FIELD_FRAGMENT: DocumentNode = gql`
	fragment MultiVersionFieldFragment on JiraMultipleVersionPickerField {
		selectedVersions {
			...Version
		}
		versions {
			nodes {
				...Version
			}
		}
	}
	${VERSION}
`;

export const MULTI_VERSION_FIELD_FRAGMENT_ASYNC_OPTIONS: DocumentNode = gql`
	fragment MultiVersionFieldFragmentAsyncOptions on JiraMultipleVersionPickerField {
		selectedVersions {
			...Version
		}
		versions(first: 1) {
			nodes {
				...Version
			}
		}
	}
	${VERSION}
`;

/* priority field */
const PRIORITY = gql`
	fragment Priority on JiraPriority {
		name
		id: priorityId
		iconUrl
	}
`;

export const PRIORITY_FIELD_FRAGMENT: DocumentNode = gql`
	fragment PriorityFieldFragment on JiraPriorityField {
		priority {
			...Priority
		}
		priorities {
			nodes {
				...Priority
			}
		}
	}
	${PRIORITY}
`;

/* issue link field */
// schema needs to be updated

/* security field */
const SECURITY_LEVEL = gql`
	fragment SecurityLevel on JiraSecurityLevel {
		id: securityId
		name
		description
	}
`;

export const ATTACHMENTS_FIELD_FRAGMENT: DocumentNode = gql`
	fragment AttachmentsFieldFragment on JiraAttachmentsField {
		maxAllowedTotalAttachmentsSize
		mediaContext {
			...JiraMediaContextFragment
		}
	}
	${MEDIA_CONTEXT_FRAGMENT}
`;

export const STORAGE_LIMITS_FRAGMENT: DocumentNode = gql`
	fragment StorageLimitsFragment on JiraQuery {
		attachmentStorageAllowed(cloudId: $cloudId, applicationKey: $applicationKey)
		attachmentStorageUsed(cloudId: $cloudId, applicationKey: $applicationKey)
		attachmentStorageIsUnlimited(cloudId: $cloudId, applicationKey: $applicationKey)
	}
`;

export const SECURITY_FIELD_FRAGMENT: DocumentNode = gql`
	fragment SecurityFieldFragment on JiraSecurityLevelField {
		securityLevel {
			...SecurityLevel
		}
		securityLevels {
			nodes {
				...SecurityLevel
			}
		}
	}
	${SECURITY_LEVEL}
`;

/* issue restriction field */
const USER_ROLES = gql`
	fragment UserRoles on JiraRole {
		roleId
	}
`;

export const ISSUE_RESTRICTION_FIELD_FRAGMENT: DocumentNode = gql`
	fragment IssueRestrictionFieldFragment on JiraIssueRestrictionField {
		selectedRoles {
			...UserRoles
		}
	}
	${USER_ROLES}
`;

/* url field */
export const URL_FIELD_FRAGMENT: DocumentNode = gql`
	fragment UrlFieldFragment on JiraUrlField {
		url
	}
`;

/* checkbox field */
const OPTION = gql`
	fragment Option on JiraOption {
		id: optionId
		value
	}
`;

export const CHECKBOX_FIELD_FRAGMENT: DocumentNode = gql`
	fragment CheckboxFieldFragment on JiraCheckboxesField {
		selectedFieldOptions {
			...Option
		}
		fieldOptions {
			nodes {
				...Option
			}
		}
	}
	${OPTION}
`;

/* single select field */
export const SINGLE_SELECT_FIELD_FRAGMENT: DocumentNode = gql`
	fragment SingleSelectFieldFragment on JiraSingleSelectField {
		fieldOption {
			...Option
		}
		fieldOptions {
			nodes {
				...Option
			}
		}
	}
	${OPTION}
`;

export const CONNECT_SINGLE_SELECT_FIELD_FRAGMENT: DocumentNode = gql`
	fragment ConnectSingleSelectFieldFragment on JiraConnectSingleSelectField {
		fieldOption {
			...Option
		}
		fieldOptions {
			nodes {
				...Option
			}
		}
	}
	${OPTION}
`;

/* multi select field */
export const MULTI_SELECT_FIELD_FRAGMENT: DocumentNode = gql`
	fragment MultiSelectFieldFragment on JiraMultipleSelectField {
		selectedFieldOptions {
			...Option
		}
		fieldOptions {
			nodes {
				...Option
			}
		}
		searchUrl
	}
	${OPTION}
`;

export const CONNECT_MULTI_SELECT_FIELD_FRAGMENT: DocumentNode = gql`
	fragment ConnectMultiSelectFieldFragment on JiraConnectMultipleSelectField {
		selectedFieldOptions {
			...Option
		}
		fieldOptions {
			nodes {
				...Option
			}
		}
	}
	${OPTION}
`;

/* radio button field */
export const RADIO_SELECT_FIELD_FRAGMENT: DocumentNode = gql`
	fragment RadioSelectFieldFragment on JiraRadioSelectField {
		selectedOption {
			...Option
		}
		fieldOptions {
			nodes {
				...Option
			}
		}
	}
	${OPTION}
`;

/* data classification field */
const CLASSIFICATION_OPTION = gql`
	fragment ClassificationLevel on JiraClassificationLevel {
		id
		name
		color {
			colorKey
		}
		definition
		status
	}
`;

export const DATA_CLASSIFICATION_FIELD_FRAGMENT: DocumentNode = gql`
	fragment DataClassificationFieldFragment on JiraDataClassificationField {
		classificationLevel {
			...ClassificationLevel
		}
		defaultClassificationLevel {
			...ClassificationLevel
		}
		classificationLevels(filterByCriteria: { filterByStatus: [PUBLISHED], filterByType: [USER] }) {
			edges {
				node {
					...ClassificationLevel
				}
			}
		}
	}
	${CLASSIFICATION_OPTION}
`;

export const DATA_CLASSIFICATION_FIELD_FRAGMENT_WITH_ORG_DEFAULT: DocumentNode = gql`
	fragment DataClassificationFieldFragmentWithOrgDefault on JiraDataClassificationField {
		classificationLevel {
			...ClassificationLevel
		}
		defaultClassificationLevel {
			...ClassificationLevel
		}
		organizationClassificationLevel {
			...ClassificationLevel
		}
		classificationLevels(filterByCriteria: { filterByStatus: [PUBLISHED], filterByType: [USER] }) {
			edges {
				node {
					...ClassificationLevel
				}
			}
		}
	}
	${CLASSIFICATION_OPTION}
`;

export const DATA_CLASSIFICATION_FIELD_FRAGMENT_WITH_CLASSIFICATION_CONTROL: DocumentNode = gql`
	fragment DataClassificationFieldFragmentWithClassificationControl on JiraDataClassificationField {
		classificationLevel {
			...ClassificationLevel
		}
		defaultClassificationLevel {
			...ClassificationLevel
		}
		organizationClassificationLevel {
			...ClassificationLevel
		}
		classificationLevels(filterByCriteria: { filterByStatus: [PUBLISHED], filterByType: [USER] }) {
			edges {
				node {
					...ClassificationLevel
				}
			}
		}
		classificationControl
		classificationLevelSource
		userOverride
		lowestLevelPermittedSource
	}
	${CLASSIFICATION_OPTION}
`;

/* group picker field fragment */
const GROUP = gql`
	fragment Group on JiraGroup {
		groupId
		name
	}
`;

export const SINGLE_GROUP_PICKER_FIELD_FRAGMENT: DocumentNode = gql`
	fragment SingleGroupPickerFieldFragment on JiraSingleGroupPickerField {
		selectedGroup {
			...Group
		}
		searchUrl
	}
	${GROUP}
`;

export const FORGE_GROUP_PICKER_FIELD_FRAGMENT: DocumentNode = gql`
	fragment ForgeGroupPickerFieldFragment on JiraForgeGroupField {
		selectedGroup {
			...Group
		}
		searchUrl
	}
	${GROUP}
`;

export const MULTI_GROUP_PICKER_FIELD_FRAGMENT: DocumentNode = gql`
	fragment MultiGroupPickerFieldFragment on JiraMultipleGroupPickerField {
		selectedGroups {
			...Group
		}
		searchUrl
	}
	${GROUP}
`;

export const FORGE_MULTI_GROUP_PICKER_FIELD_FRAGMENT: DocumentNode = gql`
	fragment ForgeMultiGroupPickerFieldFragment on JiraForgeGroupsField {
		selectedGroups {
			...Group
		}
		searchUrl
	}
	${GROUP}
`;

/* multi-line text field */
export const MULTI_LINE_TEXT_FIELD_FRAGMENT: DocumentNode = gql`
	fragment MultiLineTextFieldFragment on JiraMultiLineTextField {
		text
	}
`;

/* sprint field */
const SPRINT = gql`
	fragment Sprint on JiraSprint {
		sprintId
		name
		state
	}
`;

export const SPRINT_FIELD_FRAGMENT: DocumentNode = gql`
	fragment SprintFieldFragment on JiraSprintField {
		selectedSprints {
			...Sprint
		}
		searchUrl
	}
	${SPRINT}
`;

/* color field */
export const COLOR_FIELD_FRAGMENT: DocumentNode = gql`
	fragment ColorFieldFragment on JiraColorField {
		color {
			colorKey
		}
	}
`;

/* request type field */
const REQUEST_TYPE = gql`
	fragment RequestType on JsmRequestType {
		requestTypeId
		name
		description
		helpText
		key
		issueType {
			issueTypeId
		}
		avatar {
			large
		}
		practices {
			key
		}
	}
`;

export const REQUEST_TYPE_FIELD_FRAGMENT: DocumentNode = gql`
	fragment RequestTypeFieldFragment on JsmRequestTypeField {
		requestType {
			...RequestType
		}
		requestTypes {
			nodes {
				...RequestType
			}
		}
	}
	${REQUEST_TYPE}
`;

export const REQUEST_TYPE_FIELD_SEARCH_BY_FRAGMENT: DocumentNode = gql`
	fragment RequestTypeFieldSearchFragment on JsmRequestTypeField {
		requestType {
			...RequestType
		}
		requestTypes(searchBy: $requestTypes) {
			nodes {
				...RequestType
			}
		}
	}
	${REQUEST_TYPE}
`;

/* resolution field */

const RESOLUTION = gql`
	fragment Resolution on JiraResolution {
		name
		id: resolutionId
	}
`;

export const RESOLUTION_FIELD_FRAGMENT: DocumentNode = gql`
	fragment ResolutionFieldFragment on JiraResolutionField {
		resolution {
			...Resolution
		}
		resolutions {
			nodes {
				...Resolution
			}
		}
	}
	${RESOLUTION}
`;

export const CASCADING_SELECT_FIELD_FRAGMENT: DocumentNode = gql`
	fragment CascadingSelectFieldFragment on JiraCascadingSelectField {
		cascadingOption {
			parentOptionValue {
				...Option
			}
			childOptionValue {
				...Option
			}
		}
		cascadingOptions {
			nodes {
				parentOptionValue {
					...Option
				}
				childOptionValues {
					...Option
				}
			}
		}
	}
	${OPTION}
`;

/* team field */
const TEAM = gql`
	fragment Team on JiraTeam {
		name
		teamId
	}
`;

export const TEAM_FIELD_FRAGMENT: DocumentNode = gql`
	fragment TeamFieldFragment on JiraTeamField {
		selectedTeam {
			...Team
		}
		searchUrl
	}
	${TEAM}
`;

/* Atlassian Team field */
const ATLASSIAN_TEAM = gql`
	fragment AtlassianTeam on JiraAtlassianTeam {
		name
		teamId
		avatar {
			small
		}
	}
`;

export const ATLASSIAN_TEAM_FIELD_FRAGMENT: DocumentNode = gql`
	fragment AtlassianTeamFieldFragment on JiraAtlassianTeamField {
		JiraAtlassianTeamField: selectedTeam {
			...AtlassianTeam
		}
		searchUrl
	}
	${ATLASSIAN_TEAM}
`;

/* affected services field */
const AFFECTED_SERVICE = gql`
	fragment AffectedService on JiraAffectedService {
		serviceId
	}
`;

const ASSET = gql`
	fragment Asset on JiraAsset {
		serializedOrigin
		appKey
		originId
		value
	}
`;

export const ASSET_FIELD_FRAGMENT: DocumentNode = gql`
	fragment AssetFieldFragment on JiraAssetField {
		selectedAssets {
			...Asset
		}
		searchUrl
	}
	${ASSET}
`;

export const AFFECTED_SERVICES_FIELD_FRAGMENT: DocumentNode = gql`
	fragment AffectedServicesFieldFragment on JiraAffectedServicesField {
		selectedAffectedServices {
			...AffectedService
		}
		searchUrl
	}
	${AFFECTED_SERVICE}
`;

export const CMDB_FIELD_FRAGMENT: DocumentNode = gql`
	fragment CMDBFieldFragment on JiraCMDBField {
		searchUrl
		isMulti
		attributesIncludedInAutoCompleteSearch
		selectedCmdbObjects {
			id
			objectId
			workspaceId
			label
		}
	}
`;

/* organizations field */
const ORGANIZATION = gql`
	fragment Organization on JsmOrganization {
		organizationId
		organizationName
		domain
		__typename
	}
`;

export const ORGANIZATIONS_FIELD_FRAGMENT: DocumentNode = gql`
	fragment OrganizationsFieldFragment on JsmOrganizationField {
		selectedOrganizations {
			...Organization
		}
		searchUrl
	}
	${ORGANIZATION}
`;

// time tracking field
export const TIME_TRACKING_FIELD_FRAGMENT: DocumentNode = gql`
	fragment TimeTrackingFieldFragment on JiraTimeTrackingField {
		timeTrackingSettings {
			workingDaysPerWeek
			workingHoursPerDay
			defaultFormat
			defaultUnit
			isJiraConfiguredTimeTrackingEnabled
		}
		originalEstimate {
			timeInSeconds
		}
	}
`;

// work log field
export const WORK_LOG_FIELD_FRAGMENT: DocumentNode = gql`
	fragment WorkLogFieldFragment on JiraWorkLogField {
		timeTrackingSettings {
			workingDaysPerWeek
			workingHoursPerDay
			defaultFormat
			defaultUnit
			isJiraConfiguredTimeTrackingEnabled
		}
		mediaContext {
			...JiraMediaContextFragment
		}
	}
	${MEDIA_CONTEXT_FRAGMENT}
`;

// Parent link field
const PARENT_LINK_ISSUE: DocumentNode = gql`
	fragment Issue on JiraIssue {
		key
		issueId
		avatar {
			large
		}
	}
`;

export const PARENT_LINK_FIELD_FRAGMENT: DocumentNode = gql`
	fragment ParentLinkFieldFragment on JiraParentIssueField {
		searchUrl
		parentIssue {
			...Issue
		}
	}
	${PARENT_LINK_ISSUE}
`;

// Hierarchy unification Parent issue
const PARENT_ISSUE: DocumentNode = gql`
	fragment ParentIssue on JiraIssue {
		key
		issueId
		avatar {
			large
		}
		issueColorField {
			color {
				colorKey
			}
		}
		fieldsById(ids: ["summary", "issuetype"]) {
			edges {
				node {
					... on JiraSingleLineTextField {
						text
					}
					... on JiraIssueTypeField {
						issueType {
							name
							avatar {
								xsmall
							}
						}
					}
				}
			}
		}
	}
`;

// Hierarchy unification parent field
export const PARENT_FIELD_FRAGMENT: DocumentNode = gql`
	fragment ParentFieldFragment on JiraParentIssueField {
		parentIssue {
			...ParentIssue
		}
	}
	${PARENT_ISSUE}
`;

// Status field
export const STATUS_FIELD_FRAGMENT: DocumentNode = gql`
	fragment StatusFieldFragment on JiraStatusField {
		name
		description
		aliasFieldId
		__typename
	}
`;

export const RESPONDERS_FIELD_FRAGMENT: DocumentNode = gql`
	fragment JiraServiceManagementRespondersFieldFragment on JiraServiceManagementRespondersField {
		searchUrl
	}
`;

export const PROFORMA_CONFIG_FRAGMENT: DocumentNode = gql`
	fragment ProformaConfigFragment on JiraIssueCreateProformaConfiguration {
		serviceId
		proforma {
			portalLink
			hasProformaForm
		}
	}
`;

export const WATCHERS_CONFIG_FRAGMENT: DocumentNode = gql`
	fragment WatchersConfigFragment on JiraIssueCreateWatchersConfiguration {
		serviceId
		permissions {
			canManageWatchers
			canViewWatchers
			isAutoWatchEnabled
			isWatchersEnabled
		}
		__typename
	}
`;

export const ATLAS_PROJECT_CONFIG_FRAGMENT: DocumentNode = gql`
	fragment AtlasProjectFieldFragment on JiraTownsquareProjectField {
		atlasProject: project {
			id
			key
			name
			privateProject
			hasPermission
			isWorkspaceActive
			iconName
		}
	}
`;
